import { ref } from 'vue';
import { useRoute } from 'vue-router';
import type { ArrayElementType } from '@/core/types/gengeric';
import { useLockResource } from '@/core/composables/useLockResource';
import { emptyReplicasList, fillReplicasList, TempItemStatus, type WithReplica } from '@/core/utils/store/replicas';
import { isString } from '@/core/utils/type-guards';
import {
  type AddRoomCategoryMutationVariables,
  type EditRoomCategoryUpdateMutationVariables,
  type GetRoomCategoriesQuery,
  useEditRoomCategoryCancelMutation,
  useEditRoomCategoryKeepAliveMutation,
  useEditRoomCategoryStartMutation,
  useEditRoomCategoryUpdateMutation,
  useRemoveRoomCategoryMutation,
} from '@/graphql/generated';
import { useAddRoomCategoryMutation, useGetRoomCategoriesQuery } from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

export type RoomCategoriesObjectType = NonNullable<GetRoomCategoriesQuery['roomCategories']>;
export type RoomCategoriesType = RoomCategoriesObjectType['roomCategories'];
export type RoomCategoriesItemType = ArrayElementType<RoomCategoriesType>;

const roomCategories = ref<WithReplica<RoomCategoriesObjectType, 'roomCategories', RoomCategoriesItemType>>({
  roomCategories: emptyReplicasList(),
});
const loadedObjectCid = ref<string | null>(null);

export const useRoomCategoriesStore = () => {
  const $route = useRoute();

  const { accommodationObject } = useAccommodationObjectsStore();

  const getRoomCategories = async (force = true) => {
    const objectId = $route.params.id;

    if (!force && roomCategories.value.roomCategories.data && loadedObjectCid.value === objectId) {
      return;
    }

    if (!isString(objectId)) return;

    const { request, data, errors, response } = useGetRoomCategoriesQuery({
      accommodationObjectCid: objectId,
    });

    await request();

    if (!errors.value && data.value?.roomCategories) {
      roomCategories.value = {
        ...data.value.roomCategories,
        roomCategories: fillReplicasList(
          data.value.roomCategories.roomCategories,
          roomCategories.value.roomCategories.tempItem,
          'cid',
        ),
      };

      loadedObjectCid.value = objectId;
    }

    return response;
  };

  const setRoomCategoryById = (id: string, data: RoomCategoriesItemType) => {
    if (!roomCategories.value.roomCategories.data) return;

    const roomCategoryIndex = roomCategories.value.roomCategories.data.findIndex((item) => item.cid === id);

    if (roomCategoryIndex >= 0) {
      roomCategories.value.roomCategories.data[roomCategoryIndex] = data;
    }
  };

  const addRoomCategory = async (
    payload: AddRoomCategoryMutationVariables['addAccommodationObjectRoomCategoryInput']['roomCategory'],
  ) => {
    if (!accommodationObject.value?.cid) return;

    const { request, data } = useAddRoomCategoryMutation({
      addAccommodationObjectRoomCategoryInput: {
        accommodationObjectCid: accommodationObject.value?.cid,
        roomCategory: payload,
      },
    });

    await request();

    if (data.value?.addAccommodationObjectRoomCategory.roomCategory) {
      roomCategories.value.roomCategories.tempItem = {
        ...data.value.addAccommodationObjectRoomCategory.roomCategory,
        tempItemStatus: TempItemStatus.Added,
      };
    }

    return { data: data.value };
  };

  const editStart = async (id: string) => {
    const { request } = useEditRoomCategoryStartMutation({
      editAccommodationObjectRoomCategoryInput: { accommodationObjectRoomCategoryCid: id },
    });

    const response = await request();

    return response;
  };

  const editKeepAlive = async (id: string) => {
    const { request } = useEditRoomCategoryKeepAliveMutation({
      keepAliveAccommodationObjectRoomCategoryInput: { accommodationObjectRoomCategoryCid: id },
    });

    const response = await request();

    return response;
  };

  const editCancel = async (id: string) => {
    const { request } = useEditRoomCategoryCancelMutation({
      cancelEditAccommodationObjectRoomCategoryInput: { accommodationObjectRoomCategoryCid: id },
    });

    const response = await request();

    return response;
  };

  const editUpdate = async (
    payload: EditRoomCategoryUpdateMutationVariables['updateAccommodationObjectRoomCategoryInput'],
  ) => {
    const { request } = useEditRoomCategoryUpdateMutation({
      updateAccommodationObjectRoomCategoryInput: payload,
    });

    const response = await request();

    if (response.data?.updateAccommodationObjectRoomCategory.roomCategory) {
      roomCategories.value.roomCategories.tempItem = {
        ...response.data.updateAccommodationObjectRoomCategory.roomCategory,
        tempItemStatus: TempItemStatus.Updated,
      };
    }

    return response;
  };

  const removeRoomCategory = async (id: string) => {
    const { request } = useRemoveRoomCategoryMutation({
      input: { accommodationObjectRoomCategoryCid: id },
    });

    const response = await request();

    if (response?.data) {
      roomCategories.value.roomCategories.tempItem = {
        cid: id,
        tempItemStatus: TempItemStatus.Removed,
      };
    }

    return response;
  };

  const {
    editResourceStart: editRoomCategoryStart,
    isEditResourceStartLoading: isEditRoomCategoryStartLoading,
    editResourceCancel: editRoomCategoryCancel,
    editResourceUpdate: editRoomCategoryUpdate,
  } = useLockResource('RoomCategory', editStart, editKeepAlive, editCancel, editUpdate);

  return {
    roomCategories,

    getRoomCategories,
    setRoomCategoryById,
    addRoomCategory,
    removeRoomCategory,

    editRoomCategoryStart,
    isEditRoomCategoryStartLoading,
    editRoomCategoryCancel,
    editRoomCategoryUpdate,
  };
};
